import type { JobStatusResponseDto } from "@/api";
import { useFrameStore } from "@/lib/store/frameStore";
import { cn } from "@photo-booth/ui";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@photo-booth/ui/button";
import { ButtonGroup } from "@photo-booth/ui/button-group";
import { analyticsClick } from "@/lib/analytics/props";

interface Props {
  showResult: boolean;
  jobStatus?: JobStatusResponseDto;
  viewMode: "image" | "gif";
  onViewModeChange: (mode: "image" | "gif") => void;
  isPrinting: boolean;
}

const slideVariants = {
  hidden: {
    opacity: 0,
    y: -400,
  },
  visible: {
    opacity: 1,
    y: 0,
  },
};

const slideTransition = {
  duration: 3,
  ease: "easeOut" as const,
};

const contentVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    scale: 1,
  },
  exit: {
    opacity: 0,
  },
};

const contentTransition = {
  duration: 0.4,
  ease: "linear" as const,
  layout: {
    duration: 1,
    ease: "linear" as const,
  },
};

export const ResultDisplay: React.FC<Props> = ({
  showResult,
  viewMode,
  jobStatus,
  onViewModeChange,
  isPrinting,
}) => {
  const { frame } = useFrameStore();
  const layout = frame?.layout ?? "1x1";
  const [row, col] = layout.split("x").map(Number);
  const isVertical = row > col;

  // View mode styling
  const defaultClassName =
    "bg-natural-white text-vintage-black border-1 border-vintage-black";
  const activeClassName =
    "bg-vintage-red text-natural-white border-1 border-vintage-black";
  const getClassName = (mode: "image" | "gif") => {
    return mode === viewMode ? activeClassName : defaultClassName;
  };

  if (!jobStatus) {
    return null;
  }

  return (
    <>
      <motion.div
        initial="hidden"
        className="h-[40vh]"
        animate={showResult ? "visible" : "hidden"}
        variants={slideVariants}
        transition={slideTransition}
      >
        <div className="flex justify-center items-center w-full h-full">
          <AnimatePresence mode="wait">
            {viewMode === "image" && (
              <motion.img
                key="image"
                src={jobStatus.outputPath ?? "/mock-result.jpg"}
                alt={`Photo booth result image${frame?.name ? ` - ${frame.name} frame` : ''}`}
                loading="eager"
                className={cn(
                  "max-w-full max-h-full object-contain",
                  isVertical ? "h-full w-auto" : "w-full h-auto"
                )}
                variants={contentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                layout
                transition={contentTransition}
              />
            )}
            {viewMode === "gif" &&
              (jobStatus.videoPath ? (
                <motion.video
                  key="gif"
                  src={jobStatus.videoPath}
                  autoPlay
                  loop
                  muted
                  playsInline
                  aria-label={`Photo booth result video${frame?.name ? ` - ${frame.name} frame` : ''}`}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                  variants={contentVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  layout
                  transition={contentTransition}
                />
              ) : (
                <motion.img
                  key="gif"
                  src={jobStatus.gifPath ?? "/mock-result.gif"}
                  alt={`Photo booth result GIF animation${frame?.name ? ` - ${frame.name} frame` : ''}`}
                  loading="eager"
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                  variants={contentVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  layout
                  transition={contentTransition}
                />
              ))}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* View Mode Toggle Buttons */}
      {!isPrinting && jobStatus?.gifPath && (
        <ButtonGroup className="mt-4">
          <Button
            className={cn(getClassName("image"), "border-r-0")}
            onClick={() => onViewModeChange("image")}
            {...analyticsClick({
              event: "view-image-button",
              feature: "ui",
              location: window.location.pathname,
            })}
          >
            view image
          </Button>
          <Button
            className={getClassName("gif")}
            onClick={() => onViewModeChange("gif")}
            {...analyticsClick({
              event: "view-gif-button",
              feature: "ui",
              location: window.location.pathname,
            })}
          >
            view gif
          </Button>
        </ButtonGroup>
      )}
    </>
  );
};
